/**
 * FP3-044 Phase A — Admin Student Overview page.
 *
 * Mounts at /admin/students/:studentId. Student-centered, multi-
 * provider admin landing that replaces the read-only Student
 * Workspace as the default destination for admin navigation.
 *
 * Provider users continue to land on /students/:id via Caseload /
 * Today / Schedule (unchanged). Admin family users that try to
 * mutate provider-only surfaces are gated server-side by FP3-043
 * follow-up #9 (require_provider on /goals, /sessions, /goal-
 * evidence, etc.).
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, Buildings, GraduationCap, IdentificationCard,
  Pencil, UserPlus, Warning, Eye, Notepad, Files, Books,
  ChartLine, ClipboardText, Users, Lightbulb,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import api from "@/lib/api";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import AdminProfileTab from "@/components/admin/student/AdminProfileTab";
import AdminTeamTab from "@/components/admin/student/AdminTeamTab";
import AdminServicesTab from "@/components/admin/student/AdminServicesTab";
import AdminGoalsTab from "@/components/admin/student/AdminGoalsTab";
import AdminSessionsSummary from "@/components/admin/student/AdminSessionsSummary";
import AdminTeamNotesTab from "@/components/admin/student/AdminTeamNotesTab";
import AdminAlertsStrip from "@/components/admin/student/AdminAlertsStrip";

const TAB_KEYS = [
  "overview", "profile", "team", "services",
  "goals", "sessions", "notes", "documents", "assessments", "plan",
];

export default function AdminStudentOverview() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (() => {
    const t = searchParams.get("tab");
    return TAB_KEYS.includes(t) ? t : "overview";
  })();

  const [tab, setTab] = useState(initialTab);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api.getAdminStudentOverview(studentId)
      .then((r) => {
        if (cancelled) return;
        setData(r.data);
      })
      .catch((e) => {
        if (cancelled) return;
        const code = e?.response?.status;
        if (code === 403) setError("You don't have access to this student.");
        else if (code === 404) setError("Student not found.");
        else setError(e?.response?.data?.detail || e.message || "Failed to load student");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [studentId, reloadToken]);

  // FP3-092 — same safe background refetch StudentWorkspace uses:
  // when the tab becomes visible / window refocuses, silently reload
  // the overview so an already-open Admin page doesn't keep showing
  // stale shared profile values after a Provider edits the student.
  // Never toggles `loading` (that would unmount the tab tree and any
  // open dialogs mid-interaction).
  useEffect(() => {
    const refetch = () => {
      if (document.visibilityState !== "visible") return;
      api.getAdminStudentOverview(studentId)
        .then((r) => setData(r.data))
        .catch(() => {});
    };
    document.addEventListener("visibilitychange", refetch);
    window.addEventListener("focus", refetch);
    return () => {
      document.removeEventListener("visibilitychange", refetch);
      window.removeEventListener("focus", refetch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  // Persist active tab to the URL so deep links work.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (tab === "overview") next.delete("tab");
    else next.set("tab", tab);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const onAfterSave = () => setReloadToken((n) => n + 1);

  if (loading) {
    return (
      <div className="p-8 max-w-6xl mx-auto" data-testid="admin-student-overview-loading">
        <p className="text-sm text-[#6B7280]">Loading student overview…</p>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="p-8 max-w-6xl mx-auto" data-testid="admin-student-overview-error">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="text-[12px] text-[#6B7280] hover:text-[#1F2937] flex items-center gap-1 mb-4"
        >
          <ArrowLeft size={14} /> Back
        </button>
        <Card className="p-6 border-[#F4D1D1] bg-[#FBEEEE] text-[#A8453A]">
          <p className="text-sm font-medium">{error || "Could not load student"}</p>
        </Card>
      </div>
    );
  }

  const s = data.student;
  const fullName = `${s.first_name || ""} ${s.last_name || ""}`.trim() || "Student";
  const teamSize = Array.isArray(data.team) ? data.team.length : 0;

  return (
    <div className="p-6 max-w-6xl mx-auto animate-fade-in" data-testid="admin-student-overview">
      {/* Back chip */}
      <button
        type="button"
        onClick={() => navigate("/admin/students")}
        data-testid="admin-student-overview-back"
        className="text-[12px] text-[#6B7280] hover:text-[#1F2937] flex items-center gap-1 mb-3"
      >
        <ArrowLeft size={14} /> All students
      </button>

      {/* Sticky-style header card */}
      <Card
        data-testid="admin-student-overview-header"
        className="bg-white border-[#E5E3DB] p-5 mb-5"
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1
                data-testid="admin-student-overview-name"
                className="text-[22px] font-semibold text-[#1F2937] heading-font truncate"
              >
                {fullName}
              </h1>
              <span className="text-[11px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#F3EAD8] text-[#7A6A2C] font-medium">
                Admin view · {teamSize} provider{teamSize === 1 ? "" : "s"}
              </span>
              {s.is_archived && (
                <span className="text-[11px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#FBEEEE] text-[#A8453A] font-medium">
                  Archived
                </span>
              )}
            </div>
            <div className="text-[12px] text-[#6B7280] mt-1 flex items-center gap-3 flex-wrap">
              {s.readable_id && (
                <span className="inline-flex items-center gap-1">
                  <IdentificationCard size={12} /> {s.readable_id}
                </span>
              )}
              {s.grade !== undefined && (
                <span className="inline-flex items-center gap-1">
                  <GraduationCap size={12} /> Grade {s.grade || "—"}
                </span>
              )}
              {(s.school_name || s.campus) && (
                <span className="inline-flex items-center gap-1">
                  <Buildings size={12} /> {s.school_name || s.campus}
                </span>
              )}
              {s.teacher && (
                <span className="inline-flex items-center gap-1">
                  Teacher: {s.teacher}
                </span>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Alerts strip */}
      <AdminAlertsStrip alerts={data.alerts || []} onJumpTab={setTab} />

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        {/* FP3-082 Part 1 — Responsive tab row: horizontal-scroll at
            narrower widths instead of awkward wrap. shrink-0 on each
            trigger keeps labels intact; whitespace-nowrap on the list
            prevents wrapping so users get a smooth scroll experience
            instead of two ragged rows of tabs. */}
        <TabsList
          data-testid="admin-student-overview-tabs"
          className="bg-white border border-[#E5E3DB] rounded-lg flex flex-nowrap gap-1 p-1 mb-4 overflow-x-auto whitespace-nowrap"
        >
          <TabsTrigger value="overview" data-testid="tab-overview" className="shrink-0">
            <ChartLine size={14} className="mr-1.5" /> Overview
          </TabsTrigger>
          <TabsTrigger value="profile" data-testid="tab-profile" className="shrink-0">
            <Pencil size={14} className="mr-1.5" /> Profile
          </TabsTrigger>
          <TabsTrigger value="team" data-testid="tab-team" className="shrink-0">
            <Users size={14} className="mr-1.5" /> Team
          </TabsTrigger>
          <TabsTrigger value="services" data-testid="tab-services" className="shrink-0">
            <ClipboardText size={14} className="mr-1.5" /> Services
          </TabsTrigger>
          <TabsTrigger value="goals" data-testid="tab-goals" className="shrink-0">
            <Lightbulb size={14} className="mr-1.5" /> Goals
          </TabsTrigger>
          <TabsTrigger value="sessions" data-testid="tab-sessions" className="shrink-0">
            <Eye size={14} className="mr-1.5" /> Sessions
          </TabsTrigger>
          <TabsTrigger value="notes" data-testid="tab-notes" className="shrink-0">
            <Notepad size={14} className="mr-1.5" /> Notes
          </TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents" className="shrink-0">
            <Files size={14} className="mr-1.5" /> Documents
          </TabsTrigger>
          <TabsTrigger value="assessments" data-testid="tab-assessments" className="shrink-0">
            <Books size={14} className="mr-1.5" /> Assessments
          </TabsTrigger>
          <TabsTrigger value="plan" data-testid="tab-plan" className="shrink-0">
            <ClipboardText size={14} className="mr-1.5" /> Goal Plan
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <AdminTeamTab
            studentId={studentId}
            student={s}
            team={data.team || []}
            readOnly
            onChanged={onAfterSave}
            compactPreview
          />
          <AdminServicesTab
            rollup={data.fidelity_rollup}
            commitments={s.service_obligations || []}
            services={s.services || []}
            compactPreview
          />
          <AdminGoalsTab
            studentId={studentId}
            goalSummary={data.goal_summary || []}
            team={data.team || []}
            compactPreview
          />
          <AdminSessionsSummary summary={data.session_summary} />
        </TabsContent>

        <TabsContent value="profile">
          <AdminProfileTab
            studentId={studentId}
            student={s}
            onSaved={onAfterSave}
          />
        </TabsContent>

        <TabsContent value="team">
          <AdminTeamTab
            studentId={studentId}
            student={s}
            team={data.team || []}
            onChanged={onAfterSave}
          />
        </TabsContent>

        <TabsContent value="services">
          <AdminServicesTab
            rollup={data.fidelity_rollup}
            commitments={s.service_obligations || []}
            services={s.services || []}
          />
        </TabsContent>

        <TabsContent value="goals">
          <AdminGoalsTab
            studentId={studentId}
            goalSummary={data.goal_summary || []}
            team={data.team || []}
          />
        </TabsContent>

        <TabsContent value="sessions">
          <AdminSessionsSummary summary={data.session_summary} expanded />
        </TabsContent>

        <TabsContent value="notes">
          <AdminTeamNotesTab studentId={studentId} />
        </TabsContent>

        <TabsContent value="documents">
          <Card className="p-6" data-testid="admin-documents-readonly">
            <p className="text-sm text-[#6B7280]">
              Document management lives in the provider workspace. Open any
              provider above to view documents they uploaded for this student.
            </p>
          </Card>
        </TabsContent>

        <TabsContent value="assessments">
          <Card className="p-6" data-testid="admin-assessments-readonly">
            <p className="text-sm text-[#6B7280]">
              Assessment runner results are provider-owned. Open the relevant
              provider to view their assessment runs and articulation screener
              results for this student.
            </p>
          </Card>
        </TabsContent>

        <TabsContent value="plan">
          <Card className="p-6" data-testid="admin-plan-readonly">
            <p className="text-sm text-[#6B7280]">
              Long-Term Development and Current Focus are authored by the
              student's primary provider. Open the primary provider's workspace
              from the Team tab to view the latest plan.
            </p>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Re-export selectors for tests
export { TAB_KEYS };
